import './ResultDisplay.css';

// SVG Icons
function DownloadIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 3V15M12 15L7 10M12 15L17 10" stroke="#FF537E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5 18H19" stroke="#FF537E" strokeWidth="2" strokeLinecap="round"/>
        </svg>
    );
}

function AnimateIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M8 5.14V19.14L19 12.14L8 5.14Z" fill="#FF537E" stroke="#FF537E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

function EditIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#FF537E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="#FF537E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

// Action buttons component
function ActionButtons({ currentMedia, isVideo, onDownload, onAnimate, onEdit, canAnimate }) {
    return (
        <div className="actionButtons">
            <button className="actionButton" onClick={onDownload} title="Download">
                <DownloadIcon />
            </button>
            {!isVideo && (
                <>
                    <button
                        className="actionButton"
                        onClick={onAnimate}
                        disabled={!canAnimate}
                        title="Animate"
                    >
                        <AnimateIcon />
                    </button>
                    <button
                        className="actionButton"
                        onClick={onEdit}
                        disabled={!canAnimate}
                        title="Edit"
                    >
                        <EditIcon />
                    </button>
                </>
            )}
        </div>
    );
}

// Thumbnail component
function Thumbnail({ src, selected, onClick }) {
    if (selected) {
        return (
            <div className="thumbnailSelected" onClick={onClick}>
                <div className="thumbnailInner">
                    <img src={src} alt="" />
                </div>
            </div>
        );
    }

    return (
        <div className="thumbnail" onClick={onClick}>
            <img src={src} alt="" />
        </div>
    );
}

// Image selector - vertical strip of thumbnails
function ImageSelector({ images, selectedIndex, onSelect }) {
    if (!images || images.length <= 1) {
        return null;
    }

    return (
        <div className="imageSelector">
            {images.map((src, index) => (
                <Thumbnail
                    key={index}
                    src={src}
                    selected={selectedIndex === index}
                    onClick={() => onSelect(index)}
                />
            ))}
        </div>
    );
}

// Large image preview
function ImagePreview({ src, isVideo }) {
    if (!src) {
        return null;
    }

    if (isVideo) {
        return (
            <div className="imagePreview">
                <video src={src} autoPlay loop muted playsInline/>
            </div>
        );
    }

    return (
        <div className="imagePreview">
            <img src={src} alt="Generated result" />
        </div>
    );
}

// Empty state
function EmptyState() {
    return (
        <div className="resultEmpty">
            <div className="resultEmptyText">
                Your generated images will appear here
            </div>
        </div>
    );
}

// Loading state
function LoadingState({ genType }) {
    const text = genType === "video"
        ? "Generating video..."
        : "Generating images...";

    return (
        <div className="resultLoading">
            <div className="loadingSpinner" />
            <div className="loadingText">{text}</div>
        </div>
    );
}

// Processing overlay for animate/edit operations
function ProcessingOverlay({ actionType }) {
    const text = actionType === "video"
        ? "Creating animation..."
        : "Processing edit...";
    return (
        <div className="processingOverlay">
            <div className="loadingSpinner" />
            <div className="loadingText">{text}</div>
        </div>
    );
}

// Main ResultDisplay component
export default function ResultDisplay({
    images,
    video,
    selectedIndex,
    onSelectIndex,
    genType,
    generated,
    isProcessing,
    token,
    taskId,
    onAnimateClick,
    onEditClick
}) {
    // Download handler
    const handleDownload = async (mediaUrl, isVideo) => {
        try {
            const blob = await fetch(mediaUrl).then(res => res.blob());
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = isVideo ? "video.mp4" : "image.png";
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed:', err);
        }
    };
    // Show full loading state for NEW generation (no existing content)
    if (genType && !generated) {
        return (
            <div className="resultDisplay">
                <LoadingState genType={genType} />
            </div>
        );
    }

    // Show video if available (with optional processing overlay)
    if (video && generated) {
        return (
            <div className="resultDisplaySingle">
                <ActionButtons
                    currentMedia={video}
                    isVideo={true}
                    onDownload={() => handleDownload(video, true)}
                    onAnimate={() => {}}
                    onEdit={() => {}}
                    canAnimate={false}
                />
                <div className="imagePreviewWrapper">
                    <ImagePreview src={video} isVideo={true} />
                    {isProcessing && <ProcessingOverlay actionType={genType} />}
                </div>
            </div>
        );
    }

    // Show images if available (with optional processing overlay)
    if (images && images.length > 0 && generated) {
        const currentImage = images[selectedIndex] || images[0];
        const showThumbnails = images.length > 1;
        const canAnimate = Boolean(token && taskId);

        return (
            <div className="resultDisplay">
                <ActionButtons
                    currentMedia={currentImage}
                    isVideo={false}
                    onDownload={() => handleDownload(currentImage, false)}
                    onAnimate={() => onAnimateClick?.(selectedIndex)}
                    onEdit={() => onEditClick?.(selectedIndex)}
                    canAnimate={canAnimate}
                />
                {showThumbnails && (
                    <ImageSelector
                        images={images}
                        selectedIndex={selectedIndex}
                        onSelect={onSelectIndex}
                    />
                )}
                <div className="imagePreviewWrapper">
                    <ImagePreview src={currentImage} isVideo={false} />
                    {isProcessing && <ProcessingOverlay actionType={genType} />}
                </div>
            </div>
        );
    }

    // Show empty state
    return (
        <div className="resultDisplay">
            <EmptyState />
        </div>
    );
}
